import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import bodybuilder from 'bodybuilder'
import { DropdownComponent, DropdownOption } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import {
  AggregationType,
  DateRangesAsDropdownOptions,
  FilterType,
  toFilter
} from 'elasticsearch/constants'
import { ResponseAggregate, ResponsePayload } from 'elasticsearch/types'
import { ElasticsearchComponent, ElasticsearchProps } from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import { startCase } from 'lodash'
import React, { ComponentType } from 'react'
import { Button } from 'reactstrap'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartTitle } from '@progress/kendo-react-charts'
import { Loading } from 'components/loading'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'
import { ConnectedComponentClass } from 'react-redux'
import { Fields } from '../../shared/modals'
import * as Modals from '../../shared/modals'
import { PDFReportHeader } from './PDFReportHeader'

export class TopicPreferencesComponentClass extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      selectedDateRange: this.findDropdownOptionByValue(DateRangesAsDropdownOptions, Constants.AnalyticsFilterKeys.SELECTED_DATE_RANGE, DateRangesAsDropdownOptions[2].value),
    }
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
  }

  public render() {
    return (
      <div className="analytics-component">
        <HeaderComponent
          title={'ANALYTICS'}
          subTitle={'Topic Preferences'}
          description={[
            'Topic preference toggle activity - see which topics users are turning on and off.',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent
              data={DateRangesAsDropdownOptions}
              selected={this.state.selectedDateRange.key}
              onChange={this.dateChanged}
            />
          </div>
        </div>
        <div className="date-range-picker">
        {this.state.selectedDateRange.key === '7' ?
        <DateRangePicker onChange={this.createCustomDate}
          min={this.state.minCustomDateRange} max ={new Date()} /> : null}
        </div>
        <AnalyticsTopicPreferencesTable
          openModal={() => this.props.openModal(ExportTopicPreferencesReportStartedModal.key, {})}
          dateRange={this.state.selectedDateRange.value}
          dateRangeLabel={this.state.selectedDateRange.description}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
        />
        <div className="modal-wrapper">
          <ExportTopicPreferencesReportStartedModal data={this.props.modals} closeModal={this.props.closeModal} title="Export Started" />
        </div>
      </div>
    )
  }

  private findDropdownOptionByValue = (filters: DropdownOption[], queryKey: string, defaultValue?: string) => {
    const valueToFind = this.getQuery(queryKey, defaultValue)
    const found = filters.find((x) => x.value === valueToFind)
    return found || filters[0]
  }
}

interface TopicPreference {
  name: string,
  key: string,
  toggleOnCount?: number,
  toggleOffCount?: number,
  totalActions?: number
}

type TopicPreferences = TopicPreference[]

interface TopicPreferencesProps extends ElasticsearchProps<TopicPreferences> {
  dateRange: any,
  openModal: any,
  timezone?: string,
  useTimezoneForAnalytics?: any,
  dateRangeLabel?: string
}

interface TopicPreferencesState {
  loaded: boolean,
  isLoadingData: boolean,
}

const TOPIC_NAME_AGG: string = 'topic_name'
const ACTION_AGG: string = 'actions'

class TopicPreferencesTableComponent extends ElasticsearchComponent<TopicPreferences, TopicPreferencesProps, TopicPreferencesState> {
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()
  
  constructor(p: TopicPreferencesProps) {
    super(p, 'topic-preference-actions' as any) // Use the ES index name directly
    this.state = {
      loaded: false,
      isLoadingData: false,
    }
    this.reportName = 'Topic Preferences'
  }

  public parseResponse(payload: ResponsePayload): Promise<TopicPreferences> {
    this.setState({
      loaded: true,
      isLoadingData: false,
    })
    
    const aggregations = payload.aggregations[TOPIC_NAME_AGG]
    return Promise.resolve(aggregations.buckets.map((aggregation: ResponseAggregate) => {
      // Get the action sub-aggregation
      const actionBuckets = aggregation.child.buckets
      
      // Count on/off actions
      let toggleOnCount = 0
      let toggleOffCount = 0
      
      actionBuckets.forEach((actionBucket: any) => {
        if (actionBucket.key === 'on') {
          toggleOnCount = actionBucket.doc_count
        } else if (actionBucket.key === 'off') {
          toggleOffCount = actionBucket.doc_count
        }
      })
      
      return {
        name: startCase(aggregation.key.toLowerCase()),
        key: aggregation.key, // key is the original topic name
        toggleOnCount,
        toggleOffCount,
        totalActions: toggleOnCount + toggleOffCount,
      }
    }).filter(topic => topic.totalActions > 0)) // Only show topics with activity
  }

  public componentDidMount() {
    // Override the parent componentDidMount to handle community initialization
    this.context.user.init().then(({person, activeCommunity}) => {
      this.communityId = activeCommunity.communityId
      this.setState({ isLoadingData: true })
      this.search()
    })
  }

  public componentDidUpdate(oldProps: TopicPreferencesProps) {
    if (this.props.dateRange !== oldProps.dateRange) {
      this.setState({ isLoadingData: true })
      this.search()
    }
  }

  public searchPayload(): any {
    const search = this.generateSearchQuery()
    
    // Add topic name aggregation with sub-aggregation for actions
    return this.addAggregation(search, {
      type: AggregationType.terms,
      field: 'topic_name.keyword',
      name: TOPIC_NAME_AGG,
      extra: { size: Constants.ANALYTICS_TOPICS_LIMIT || 100 },
      child: {
        type: AggregationType.terms,
        field: 'action.keyword',
        name: ACTION_AGG,
        unique_on: 'community_person_id',
        extra: { size: Constants.ANALYTICS_TOPICS_LIMIT },
      },
    }).build()
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current

      // Temporarily add PDF export mode class to show PDF elements
      element.classList.add('pdf-export-mode')

      // Wait a brief moment for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100))

      const pdf = new jsPDF('l', 'mm', 'a4') // landscape orientation
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      // Capture header and chart separately (Page 1)
      const headerChart = element.querySelector('.pdf-header-chart-section')
      if (headerChart) {
        const headerCanvas = await html2canvas(headerChart as HTMLElement, {
          height: headerChart.scrollHeight,
          width: headerChart.scrollWidth,
          useCORS: true,
          scale: 2
        })

        const headerImgData = headerCanvas.toDataURL('image/png')
        const headerRatio = Math.min(pdfWidth / headerCanvas.width, pdfHeight / headerCanvas.height)
        const headerImgX = (pdfWidth - headerCanvas.width * headerRatio) / 2

        pdf.addImage(headerImgData, 'PNG', headerImgX, 10, headerCanvas.width * headerRatio, headerCanvas.height * headerRatio)
      }

      // Capture table separately (Page 2+)
      const tableSection = element.querySelector('.pdf-table-section')
      if (tableSection && this.props.results && this.props.results.length > 0) {
        const itemsPerPage = 20
        const totalPages = Math.ceil(this.props.results.length / itemsPerPage)

        for (let page = 0; page < totalPages; page++) {
          pdf.addPage()

          // Add page header
          pdf.setFontSize(16)
          pdf.text(`Topic Preferences Report - Page ${page + 2} of ${totalPages + 1}`, 20, 20)
          pdf.setFontSize(12)
          pdf.text(`Showing entries ${page * itemsPerPage + 1} - ${Math.min((page + 1) * itemsPerPage, this.props.results.length)} of ${this.props.results.length}`, 20, 30)

          // Create table data for this page
          const pageData = this.props.results.slice(page * itemsPerPage, (page + 1) * itemsPerPage)

          // Simple table rendering
          let yPosition = 50
          const rowHeight = 8
          const colPositions = [20, 120, 180, 240]

          // Table headers
          pdf.setFontSize(10)
          pdf.setFont(undefined, 'bold')
          pdf.text('Topic', colPositions[0], yPosition)
          pdf.text('Toggle On', colPositions[1], yPosition)
          pdf.text('Toggle Off', colPositions[2], yPosition)
          pdf.text('Total Actions', colPositions[3], yPosition)
          yPosition += rowHeight + 2

          // Table rows
          pdf.setFont(undefined, 'normal')
          pageData.forEach((item: any) => {
            if (yPosition > pdfHeight - 20) return // Stop if near bottom of page

            pdf.text(item.name ? item.name.substring(0, 30) : 'N/A', colPositions[0], yPosition)
            pdf.text((item.toggleOnCount || 0).toString(), colPositions[1], yPosition)
            pdf.text((item.toggleOffCount || 0).toString(), colPositions[2], yPosition)
            pdf.text((item.totalActions || 0).toString(), colPositions[3], yPosition)
            yPosition += rowHeight
          })
        }
      }

      // Remove PDF export mode class
      element.classList.remove('pdf-export-mode')

      pdf.save('topic-preferences-report.pdf')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error)
      // Make sure to remove the class even if there's an error
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  public render = () => <div>
    <div className="topic-preferences-chart">
      {this.state.isLoadingData ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loading size="32" />
        </div>
      ) : (this.props.results && this.props.results.length > 0 ?
      <div>
        <div className="articles-export-buttons">
          <Button
            disabled={this.props.results.length < 1 || this.state.isLoadingData}
            onClick={() => this.xlsxExport.save()}>
            Export xlsx
          </Button>
          <Button
            color="secondary"
            disabled={this.props.results.length < 1 || this.state.isLoadingData}
            onClick={this.exportToPDF}>
            Export PDF
          </Button>
        </div>
        <div ref={this.pdfExportRef}>
          <div className="pdf-header-chart-section">
            <PDFReportHeader
              title="Topic Preferences Report"
              dateRange={this.props.dateRange}
              dateRangeLabel={this.props.dateRangeLabel}
              fields={[]}
              analysis="Topic preference toggle activity showing on/off action counts"
            />

            {this.props.results && this.props.results.length > 0 && (
              <div className="pdf-chart-section">
                <h3>Top 20 Topics - Toggle Activity</h3>
                <Chart style={{ height: '400px' }}>
                  <ChartTitle text="Topic Preference Activity" />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={this.props.results.slice(0, 20).map((item: any) =>
                        item.name ? item.name.substring(0, 15) + '...' : 'Untitled'
                      )}
                      labels={{ rotation: -45 }}
                    />
                  </ChartCategoryAxis>
                  <ChartSeries>
                    <ChartSeriesItem
                      type="column"
                      data={this.props.results.slice(0, 20).map((item: any) => item.toggleOnCount || 0)}
                      name="Toggle On"
                      color="#28a745"
                    />
                    <ChartSeriesItem
                      type="column"
                      data={this.props.results.slice(0, 20).map((item: any) => item.toggleOffCount || 0)}
                      name="Toggle Off"
                      color="#dc3545"
                    />
                  </ChartSeries>
                </Chart>
              </div>
            )}
          </div>

          <div className="pdf-table-section">
            <h3>Detailed Topic Preference Activity</h3>
            <Grid data={this.props.results} className="analytics-counts-grid" scrollable="none">
              <GridColumn field="name" title="Topic" />
              <GridColumn field="toggleOnCount" title="Toggle On Count"/>
              <GridColumn field="toggleOffCount" title="Toggle Off Count"/>
              <GridColumn field="totalActions" title="Total Actions"/>
            </Grid>
          </div>
        </div>
        <ExcelExport data={this.props.results}
          fileName="TopicPreferencesReport.xlsx"
          ref={(exporter) => {this.xlsxExport = exporter}}>
          <Grid data={this.props.results} className="analytics-counts-grid" scrollable="none">
            <GridColumn field="name" title="Topic" />
            <GridColumn field="toggleOnCount" title="Toggle On Count"/>
            <GridColumn field="toggleOffCount" title="Toggle Off Count"/>
            <GridColumn field="totalActions" title="Total Actions"/>
          </Grid>
        </ExcelExport>
      </div> :
      <div>
        <p className="no-data-tag">
          {Constants.NO_DATA_COPY}
        </p>
      </div>)}
    </div>
  </div>

  private generateSearchQuery = (): bodybuilder.Bodybuilder => {
    const search = bodybuilder()
      .size(0)
      .filter(FilterType.range, 'created', toFilter(this.props.dateRange, this.props.timezone, this.props.useTimezoneForAnalytics))
    
    return search
  }
}

class ExportTopicPreferencesReportStartedModal extends Modals.ModalComponent {
  public static key: string = 'topicPreferencesReportExportStart'
  constructor(props: Modals.ModalComponentProps) {
    super(props, ExportTopicPreferencesReportStartedModal.key)
  }
  protected renderChildren(data: any) {
    return 'We are exporting your topic preferences report now. Check your inbox for a link to download the list.'
  }
}

export const AnalyticsTopicPreferencesTable: ConnectedComponentClass<ComponentType<TopicPreferencesTableComponent>, Fields> = GenericRedux.registerNewComponent(
  TopicPreferencesTableComponent, 'home_topic_preferences', {})

export const TopicPreferencesComponent = GenericRedux.registerNewComponentWithModals(
  TopicPreferencesComponentClass,
  'analytics_topic_preferences_modal',
  [ExportTopicPreferencesReportStartedModal.key],
  {}
)